import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import {
  Box,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'next-i18next';
import React, { useCallback } from 'react';
import type { FC } from 'react';

import Button from 'components/Button';
import CustomListItemButton from 'components/MuiCustom/ListItemButton';

import type { AddMemberToOrganizationModalStageProps } from '..';

const AssignRoleToUser: FC<AddMemberToOrganizationModalStageProps> = ({
  state,
  setState,
  onCloseProxy,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const userRoles = [
    {
      role: t('common:pages.organization_overview.add_user.role_user'),
      info: t('common:pages.organization_overview.add_user.role_user_info'),
      value: 'member',
    },
    {
      role: t('common:pages.organization_overview.add_user.role_admin'),
      info: t('common:pages.organization_overview.add_user.role_admin_info'),
      value: 'org_admin',
    },
  ];

  const handleAssignRole = useCallback(() => {
    if (!state.role) return;

    setState(prev => ({
      ...prev,
      stage: 'devices',
    }));
  }, [setState, state.role]);

  return (
    <>
      <Box mb={4}>
        <Typography
          variant="h2"
          mb={2}
          textAlign="center"
          data-testid="select-role-title"
        >
          {t('common:pages.organization_overview.add_user.select_role')}
        </Typography>
        <Typography variant="body1" textAlign="center">
          {t('common:pages.organization_overview.add_user.select_role_desc')}
        </Typography>
      </Box>
      <Box mb={4} width="100%" minHeight={150}>
        {userRoles.map((item, index) => {
          const isFirstItem = index === 0;
          const isLastItem = index === userRoles.length - 1;

          return (
            <ListItem disablePadding key={item.role}>
              <CustomListItemButton
                sx={{
                  backgroundColor: 'grey.100',
                  borderBottom: isLastItem
                    ? 0
                    : `1px solid ${theme.vars.palette.grey[300]}`,
                  borderTopLeftRadius: isFirstItem ? 16 : 0,
                  borderTopRightRadius: isFirstItem ? 16 : 0,
                  borderBottomLeftRadius: isLastItem ? 16 : 0,
                  borderBottomRightRadius: isLastItem ? 16 : 0,
                }}
                onClick={() =>
                  setState(prev => ({ ...prev, role: item.value }))
                }
                data-testid={`set-role-${item.value}-button`}
              >
                <Box mr={2} alignItems="center" display="flex">
                  <CheckIcon
                    sx={{
                      color:
                        state.role === item.value
                          ? 'primary.main'
                          : 'transparent',
                    }}
                  />
                </Box>
                <ListItemText primary={item.role} secondary={item.info} />
              </CustomListItemButton>
            </ListItem>
          );
        })}
      </Box>
      <Box
        width="100%"
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        mt={2}
        gap={2}
      >
        <Button
          variant="flat"
          color="neutral"
          size="large"
          onClick={onCloseProxy}
          startIcon={<KeyboardArrowLeftRoundedIcon />}
          data-testid="modal-cancel-button"
        >
          {t('common:previous')}
        </Button>
        <Button
          variant="flat"
          onClick={handleAssignRole}
          size="large"
          disabled={state.role.length === 0}
          endIcon={<KeyboardArrowRightRoundedIcon />}
          data-testid="modal-save-button"
        >
          {t('common:next')}
        </Button>
      </Box>
    </>
  );
};

export default AssignRoleToUser;
